import { action, makeObservable } from 'mobx';

import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import HostStore from 'stores/host/HostStore';
import { SortDirection } from 'constants/SortDirection';
import { IDataFetchOptions } from '../../types';
import getAnalysisContext from '../../helpers/getAnalysisContext';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import { buildOptions, get, getCount, getPage } from 'stores/_services/dataService';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';

const SEARCH_FIELDS = ['hostname', 'os'];

interface IHostsFrom {
    itemKey: string;
    route: string;
}

export const HostsFrom: Record<string, IHostsFrom> = {
    PROCESS: { itemKey: 'fileRepId', route: 'ProcessHosts' },
    MODULE: { itemKey: 'fileRepId', route: 'ModuleHosts' },
    DRIVER: { itemKey: 'fileRepId', route: 'DriverHosts' },
    ACCOUNT: { itemKey: 'accountId', route: 'AccountHosts' },
    ARTIFACT: { itemKey: 'fileRepId', route: 'ArtifactHosts' },
    AUTOSTART: { itemKey: 'fileRepId', route: 'AutostartHosts' },
    SCRIPT: { itemKey: 'fileRepId', route: 'ScriptHosts' },
    MEMORY: { itemKey: 'fileRepId', route: 'MemscanHosts' }
};

export default class HostListStore extends BaseListStore<HostStore> {
    readonly itemKey?: string;
    readonly itemId?: string;
    readonly route: string;

    constructor(from?: IHostsFrom, itemId?: string) {
        super({
            itemStore: HostStore,
            sortBy: 'lastSeenOn',
            sortDirection: SortDirection.DESC,
            defaultFilter: {
                compromised: false,
                uncompromised: false,
                search: ''
            }
        });

        makeObservable(this, {
            getHosts: action,
            getHost: action,
            getTotalCount: action
        });

        this.route = from?.route || 'hosts';
        this.itemKey = from?.itemKey;
        this.itemId = itemId;
    }

    async getHosts(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const { scanId, boxId, route } = await getAnalysisContext(this.route);
        const conditions = getConditions(this.filter) as Record<string, unknown>[];
        conditions.push({ scanId, boxId });

        if (this.itemId && this.itemKey) {
            conditions.push({ [this.itemKey]: this.itemId });
        }

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const { data } = await getPage(route, options);
        const totalCount = await getCount(route, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        this.count = data.length;
        this.totalCount = totalCount;
        this.page = pageNumber;
    }

    async getHost(id: string, overrideRoute?: string): Promise<void> {
        const context = await getAnalysisContext(this.route);
        const route = overrideRoute || context.route;
        const { data } = await get(`${route}/${id}`);

        this._updateData(data);
    }

    async getTotalCount(): Promise<void> {
        const { analysisFilter } = await getAnalysisContext(this.route);

        let where = analysisFilter;

        if (this.itemId && this.itemKey) {
            where = { ...where, [this.itemKey]: this.itemId };
        }

        this.totalCount = await getCount(this.route, where);
    }

    public async exportToCsv(): Promise<void> {
        const { scanId, boxId, route } = await getAnalysisContext(this.route);
        const conditions = getListConditions(this.selectedIdList, this.filter);
        conditions.push({ scanId, boxId });

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            },
            excludeFields: ['id', 'boxId', 'hostId', 'scanId']
        };

        const query = buildOptions(options);
        window.location.href = await buildCsvDownloadUrl(route, query);
    }
}
